import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { RevealGroup, RevealItem } from "@/components/site/Reveal";
import api from "@/lib/api";
import { IMAGES } from "@/lib/content";

const FALLBACK = [
  { slug: "monaco", name: "Monaco", tagline: "Europe's most concentrated private wealth ecosystem", image: IMAGES.monaco, stats: [] },
  { slug: "geneva", name: "Geneva", tagline: "The global capital of cross-border private banking", image: IMAGES.geneva, stats: [] },
  { slug: "paris", name: "Paris", tagline: "A resurgent European financial centre", image: IMAGES.paris, stats: [] },
];

export default function Markets() {
  const [markets, setMarkets] = useState(FALLBACK);
  useEffect(() => { api.getMarkets().then((m) => m && m.length && setMarkets(m)).catch(() => {}); }, []);

  return (
    <div data-testid="markets-page">
      <PageHero
        breadcrumb={[{ label: "Markets" }]}
        eyebrow="Where we operate"
        title="Dual-market intelligence no generalist can replicate."
        intro="Genuine, on-the-ground presence across Monaco, Geneva and Paris — with proprietary insight into compensation, mobility and demand between them."
      />

      <section className="section-py pt-6">
        <div className="container-bp">
          <RevealGroup className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {markets.map((m) => (
              <RevealItem key={m.slug}>
                <Link to={`/markets/${m.slug}`} data-testid={`market-card-${m.slug}`} className="group block overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50 hover:shadow-[var(--shadow-lift)]">
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img src={m.image} alt={m.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bp-surface)] via-transparent to-transparent" />
                    <h3 className="absolute bottom-4 left-5 font-display text-3xl font-semibold text-[color:var(--bp-cream)]">{m.name}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-[14px] text-[color:var(--bp-text-muted)]">{m.tagline}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-[color:var(--bp-teal)]">Explore {m.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <CTABand title="Need talent intelligence on a specific market?" />
    </div>
  );
}
